/**
 * Webhooks API Service
 *
 * Client service for communicating with the Webhooks API for subscription
 * management, secret rotation, and delivery history.
 *
 * The Webhooks API handles webhook subscription CRUD, HMAC secret management,
 * and delivery tracking. All endpoints require Bearer token authentication.
 *
 * Uses Platform_Core's createApiClient for authenticated HTTP calls.
 * Token injection is handled automatically via the authClient configuration.
 */

import { createApiClient, type PlatformApiClient } from '@internal/platform-core';
import { tokenService } from './token.service';
import { WEBHOOKS_API_URL } from '../config/api.config';

/**
 * Webhook subscription record
 */
export interface WebhookSubscription {
  subscriptionId: string;
  targetUrl: string;
  eventTypes: string[];
  status: 'active' | 'paused' | 'disabled';
  description?: string;
  customHeaders?: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

/**
 * Request body for creating a new subscription
 */
export interface CreateSubscriptionRequest {
  targetUrl: string;
  eventTypes: string[];
  description?: string;
  customHeaders?: Record<string, string>;
}

/**
 * Response from creating a subscription (includes one-time HMAC secret)
 */
export interface CreateSubscriptionResponse {
  subscription: WebhookSubscription;
  secret: string; // HMAC secret, shown only once
}

/**
 * Request body for updating an existing subscription
 */
export interface UpdateSubscriptionRequest {
  status?: 'active' | 'paused';
  targetUrl?: string;
  eventTypes?: string[];
  description?: string;
  customHeaders?: Record<string, string>;
}

/**
 * Webhook delivery attempt record
 */
export interface WebhookDelivery {
  deliveryId: string;
  subscriptionId: string;
  eventType: string;
  timestamp: string;
  httpStatusCode: number | null;
  success: boolean;
  errorMessage?: string;
}

/**
 * Webhooks API Service Class
 *
 * Uses Platform_Core's createApiClient for all HTTP calls.
 * Token injection is handled automatically via the authClient config.
 */
export class WebhooksService {
  private api: PlatformApiClient;

  constructor(baseUrl?: string) {
    this.api = createApiClient({
      baseUrl: baseUrl || WEBHOOKS_API_URL,
      authClient: {
        getAccessToken: () => tokenService.getAccessToken(),
        refreshToken: async () => false, // Auth-portal manages its own refresh
      },
    });
  }

  /**
   * List all webhook subscriptions for the authenticated user
   */
  async listSubscriptions(): Promise<WebhookSubscription[]> {
    return this.api.get<WebhookSubscription[]>('/subscriptions');
  }

  /**
   * Create a new webhook subscription
   * Returns the subscription and a one-time HMAC secret
   */
  async createSubscription(data: CreateSubscriptionRequest): Promise<CreateSubscriptionResponse> {
    return this.api.post<CreateSubscriptionResponse>('/subscriptions', data);
  }

  /**
   * Update an existing webhook subscription
   */
  async updateSubscription(subscriptionId: string, data: UpdateSubscriptionRequest): Promise<WebhookSubscription> {
    return this.api.put<WebhookSubscription>(`/subscriptions/${encodeURIComponent(subscriptionId)}`, data);
  }

  /**
   * Delete a webhook subscription
   */
  async deleteSubscription(subscriptionId: string): Promise<void> {
    await this.api.delete(`/subscriptions/${encodeURIComponent(subscriptionId)}`);
  }

  /**
   * Rotate the HMAC signing secret for a subscription
   * Returns the new secret (shown only once)
   */
  async rotateSecret(subscriptionId: string): Promise<{ secret: string }> {
    return this.api.post<{ secret: string }>(`/subscriptions/${encodeURIComponent(subscriptionId)}/rotate-secret`);
  }

  /**
   * List delivery history for a subscription
   */
  async listDeliveries(subscriptionId: string): Promise<WebhookDelivery[]> {
    return this.api.get<WebhookDelivery[]>(`/subscriptions/${encodeURIComponent(subscriptionId)}/deliveries`);
  }
}

// Export singleton instance
export const webhooksService = new WebhooksService();
